import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { useAuth, useLoginMutation } from '@/hooks/useAuth';
import { ApiError } from '@/lib/api';

const loginSchema = z.object({
  password: z.string().min(8, 'Mindestens 8 Zeichen.'),
});
type LoginValues = z.infer<typeof loginSchema>;

const searchSchema = z.object({
  redirect: z.string().optional(),
});

export const Route = createFileRoute('/login')({
  component: LoginPage,
  validateSearch: (search) => searchSchema.parse(search),
});

function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const { redirect } = useSearch({ from: '/login' });
  const { isAuthenticated } = useAuth();
  const login = useLoginMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginValues>({
    defaultValues: { password: '' },
  });

  // Bei bereits gültigem Token: weiter.
  useEffect(() => {
    if (isAuthenticated) {
      void navigate({ to: redirect ?? '/', replace: true });
    }
  }, [isAuthenticated, navigate, redirect]);

  const onSubmit = handleSubmit((values) => {
    const parsed = loginSchema.safeParse(values);
    if (!parsed.success) {
      const flat = parsed.error.flatten();
      if (flat.fieldErrors.password?.[0]) {
        setError('password', { message: flat.fieldErrors.password[0] });
      }
      return;
    }
    login.mutate(parsed.data, {
      onSuccess: () => {
        void navigate({ to: redirect ?? '/', replace: true });
      },
      onError: (err) => {
        const msg =
          err instanceof ApiError
            ? err.status === 401
              ? 'Master-Passwort ungültig.'
              : (err.problem?.detail ?? err.problem?.title ?? 'Login fehlgeschlagen.')
            : 'Login fehlgeschlagen.';
        setError('password', { message: msg });
      },
    });
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-4">
      <Card className="w-full max-w-md" title="ITKFM Wartungstool" description="Anmeldung">
        <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <Input
            type="password"
            label="Master-Passwort"
            autoComplete="current-password"
            autoFocus
            error={errors.password?.message}
            {...register('password')}
          />
          <Button type="submit" loading={login.isPending} className="w-full">
            Anmelden
          </Button>
          <p className="text-xs text-muted">
            Lokales Single-User-Tool. Token wird im Browser-Storage abgelegt.
          </p>
        </form>
      </Card>
    </div>
  );
}
